import { logger } from '@mail-core/logger';
import type { PayloadAction } from '@reduxjs/toolkit';
import { extInfo } from 'lib/extInfo';
import { getExtension, isVirusItem } from 'reactApp/modules/file/utils';
import { changeGalleryCategory, galleryLoadSuccess } from 'reactApp/modules/gallery/gallery.module';
import { GalleryCategoryType } from 'reactApp/modules/gallery/gallery.types';
import { addFilesSuccess } from 'reactApp/modules/modifying/modifying.actions';
import type { AddFileSuccessAction } from 'reactApp/modules/modifying/modifying.types';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { showVirusDlg } from 'reactApp/modules/popup/popup.module';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { changeHistory, routeStatusPage } from 'reactApp/modules/router/router.module';
import { resetSelected } from 'reactApp/modules/selections/selection.saga';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { getItemsCount } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { IInputFile } from 'reactApp/modules/uploadList/uploadList.model';
import { getInputFile } from 'reactApp/modules/uploadList/uploadList.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { openSelectFromCloudPopup } from 'reactApp/sections/AlbumsPage/SelectFromCloudPopup/SelectFromCloudPopup.helpers';
import { EAction } from 'reactApp/sections/AlbumsPage/SelectFromCloudPopup/SelectFromCloudPopup.types';
import { EStatus } from 'reactApp/sections/ErrorPage/ErrorPage.types';
import { renderNewVigboAlbumModal } from 'reactApp/sections/VigboPage/renderNewVigboAlbumModal';
import { store as reduxStore } from 'reactApp/store';
import { openDisabledFeaturePopupHelper } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.helpers';
import { DisabledFeature } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.types';
import { closeSelectVigboAlbumPopup, openSelectVigboAlbumPopup } from 'reactApp/ui/SelectVigboAlbumPopup/SelectVigboAlbumPopup.helpers';
import { ActionName } from 'reactApp/ui/VirusDialog/VirusDialog.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { channel } from 'redux-saga';
import { all, call, put, race, select, take, takeEvery, takeLatest } from 'typed-redux-saga';

import { vigboAlbumErrors } from './vigbo.constants';
import { requestAddToVigboAlbum, requestCreateVigboAlbum, requestVigboAlbum, requestVigboAlbumsList } from './vigbo.helpers';
import {
    createVigboAlbumFail,
    createVigboAlbumRequest,
    createVigboAlbumSuccess,
    loadVigboAlbumFail,
    loadVigboAlbumRequest,
    loadVigboAlbumsListFail,
    loadVigboAlbumsListRequest,
    loadVigboAlbumsListSuccess,
    loadVigboAlbumSuccess,
    selectItemsFromCloudToVigboAlbum,
    selectVigboAlbumAndAddItems,
} from './vigbo.module';
import { getVigboAlbumById, hasActiveVigboTariff } from './vigbo.selectors';
import type {
    CreateVigboAlbumRequestPayload,
    CreateVigboAlbumsSuccessPayload,
    SelectVigboAlbumAndAddItemsPayload,
    VigboAlbumId,
} from './vigbo.types';

function* handleVigboAlbumsListRequest() {
    try {
        const { data } = yield* call(requestVigboAlbumsList);
        yield* put(loadVigboAlbumsListSuccess(data));
    } catch (error) {
        yield* put(loadVigboAlbumsListFail());
        logger.error(error);
    }
}

function* handleVigboAlbumRequest(action: PayloadAction<VigboAlbumId>) {
    const { albumId } = action.payload;
    const currentAlbum = yield* select(getVigboAlbumById, albumId);
    const cursor = currentAlbum?.cursor;

    try {
        const { data } = yield* call(requestVigboAlbum, { album_id: albumId, cursor });
        yield* put(loadVigboAlbumSuccess({ ...data, albumId }));
    } catch (error: any) {
        logger.error(error);

        if (error.status === 404) {
            yield* put(routeStatusPage({ status: EStatus.NOT_FOUND }));
            return;
        }

        yield* put(loadVigboAlbumFail({ albumId }));
    }
}

function* handleCreateVigboAlbumRequest(action: PayloadAction<CreateVigboAlbumRequestPayload>) {
    const { title, navigateToAlbum } = action.payload;
    try {
        const { data } = yield* call(requestCreateVigboAlbum, { title });
        yield* put(createVigboAlbumSuccess({ ...data, navigateToAlbum }));
    } catch (error: any) {
        logger.error(error);
        yield* put(createVigboAlbumFail());
    } finally {
        closePopupHelper(popupNames.CREATE_FOLDER);
    }
}

function* handleSelectVigboAlbumAndAddItems(action: PayloadAction<SelectVigboAlbumAndAddItemsPayload>) {
    const { items } = action.payload;

    if (!items?.length) {
        return;
    }

    if (items.length && items.some(isVirusItem)) {
        yield put(showVirusDlg({ items, actionName: ActionName.publish, doNotChangeRoute: true }));
        return;
    }

    const { isFull: isOverquota } = yield* select(UserSelectors.getCloudSpace);
    const isCorpUser = yield select(UserSelectors.isCorpUser);
    const ignoreUserOverquota = isCorpUser;
    if (isOverquota && !ignoreUserOverquota) {
        openDisabledFeaturePopupHelper({ disabledFeature: DisabledFeature.createAlbum });
        return;
    }

    const selectAlbumChanel = channel();
    openSelectVigboAlbumPopup({
        onClose: () => selectAlbumChanel.close(),
        onSuccess: (albumId: string) => selectAlbumChanel.put(albumId),
        onCreateAlbum: () => {
            selectAlbumChanel.put(EAction.CREATE);
            sendPaymentGa({
                eventCategory: ECategoryGa.vigbo,
                action: 'create-gallery-click',
                place: 'choose-gallery',
            });
        },
    });
    let albumId: string = yield take(selectAlbumChanel);
    closeSelectVigboAlbumPopup();
    selectAlbumChanel.close();

    if (albumId === EAction.CREATE) {
        const createAlbumChannel = channel<string>();

        yield* call(renderNewVigboAlbumModal, {
            onClose: () => createAlbumChannel.put('close'),
            navigateToAlbum: false,
        });

        const { success, fail, close } = yield* race({
            success: take<PayloadAction<CreateVigboAlbumsSuccessPayload>>(createVigboAlbumSuccess.type),
            fail: take<PayloadAction>(createVigboAlbumFail.type),
            close: take(createAlbumChannel),
        });

        if (fail || close) {
            return;
        }

        /** Так как сделали проверку на fail и close, то success точно определен */
        albumId = success!.payload.id;
    }

    try {
        yield* call(requestAddToVigboAlbum, { paths: items.map((item) => item.id), album_id: albumId });
        yield* put(
            showSnackbarAction({
                id: 'vigbo-add-to-album-success',
                text: 'Успешно добавлено в галерею',
                type: SnackbarTypes.success,
                closable: true,
                buttonText: 'Посмотреть',
                onButtonClick: () => {
                    reduxStore.dispatch(changeHistory(`/vigbo/${albumId}`));
                },
            })
        );
    } catch (error) {
        yield* put(
            showSnackbarAction({
                id: 'error',
                text: vigboAlbumErrors.getMessage('add_error'),
                type: SnackbarTypes.failure,
                closable: true,
            })
        );

        logger.error(error);
    }

    yield resetSelected();
}

function* handleSelectItemsFromCloud(action: PayloadAction<VigboAlbumId>) {
    const isVigboActiveTariff = yield* select(hasActiveVigboTariff);
    if (!isVigboActiveTariff) {
        yield put(
            showSnackbarAction({
                closable: true,
                id: 'add-from-cloud-error',
                type: SnackbarTypes.failure,
                text: 'Вы можете только просматривать содержимое галереи, загружать в нее нельзя',
            })
        );

        return;
    }

    const { albumId } = action.payload;

    yield* resetSelected();

    yield* put(changeGalleryCategory({ category: GalleryCategoryType.all }));
    yield* take(galleryLoadSuccess.type);

    const datalistChannel = channel();
    const count = yield select(getItemsCount, EStorageType.gallery);

    openSelectFromCloudPopup({
        onClose: () => datalistChannel.close(),
        onSuccess: () => datalistChannel.put(true),
        titleText: 'Добавить в галерею',
        submitBttnText: 'Добавить в галерею',
        isEmpty: !count,
        emptyText: 'Загрузите фото или видео, чтобы добавить их в галерею',
    });

    yield* take(datalistChannel);
    datalistChannel.close();

    const ids = yield select(SelectionsSelectors.getSelectedIdxs);

    try {
        if (!ids.length) {
            return;
        }

        yield* call(requestAddToVigboAlbum, { paths: ids, album_id: albumId });
        yield* put(loadVigboAlbumRequest({ albumId }));

        yield* put(
            showSnackbarAction({
                id: 'vigbo-album-success',
                text: `${ids.length > 1 ? 'Файлы добавлены' : 'Файл добавлен'} в Галерею Vigbo`,
                type: SnackbarTypes.success,
                closable: true,
            })
        );
        sendPaymentGa({
            eventCategory: ECategoryGa.vigbo,
            action: 'upload-file',
            length: ids.length,
        });
    } catch (error: any) {
        logger.error(error);

        yield* put(
            showSnackbarAction({
                id: 'vigbo-album-error',
                text: vigboAlbumErrors.getMessage('add_error'),
                type: SnackbarTypes.failure,
                closable: true,
            })
        );
    }
}

function* handleCreateVigboAlbumSuccess(action: PayloadAction<CreateVigboAlbumsSuccessPayload>) {
    const { id, navigateToAlbum } = action.payload;

    if (navigateToAlbum) {
        yield* put(changeHistory(`/vigbo/${id}`));
    }
}

function* handleAddFilesFromDevice(action: PayloadAction<AddFileSuccessAction>) {
    const { items: list, storage } = action.payload;

    if (storage !== EStorageType.vigbo) {
        return;
    }

    const allowedExtensions = extInfo.getExtsForVigboAlbums();
    const items = list.filter((item) => {
        const ext = getExtension(item);
        return allowedExtensions.includes(ext?.toLowerCase());
    });
    if (!items?.length) {
        return;
    }

    for (const item of items) {
        sendPaymentGa({
            eventCategory: ECategoryGa.vigbo,
            action: 'upload-file',
            ext: getExtension(item),
            size: item?.size,
        });
    }

    const ids = items.map((item) => item.home).filter(Boolean);
    const albumItems = yield* all(ids.map((id) => select((state) => getInputFile(state, { cloudPath: id })))) || [];
    const albumsMap = albumItems.reduce<Record<string, IInputFile[]>>((albumIdMap, file) => {
        if (!file?.albumId) {
            return albumIdMap;
        }

        const albumList = albumIdMap[file.albumId] || [];
        albumList.push(file);
        albumIdMap[file.albumId] = albumList;

        return albumIdMap;
    }, {});

    for (const albumId of Object.keys(albumsMap)) {
        const itemPaths = albumsMap[albumId].map((item) => item.cloudPath).filter(Boolean);
        yield* call(requestAddToVigboAlbum, { paths: itemPaths, album_id: albumId });
        yield* put(loadVigboAlbumRequest({ albumId }));
    }
}

export function* watchVigbo() {
    yield* takeEvery(createVigboAlbumRequest.type, handleCreateVigboAlbumRequest);
    yield* takeEvery(createVigboAlbumSuccess.type, handleCreateVigboAlbumSuccess);
    yield* takeLatest(loadVigboAlbumsListRequest.type, handleVigboAlbumsListRequest);
    yield* takeLatest(loadVigboAlbumRequest.type, handleVigboAlbumRequest);
    yield* takeEvery(selectItemsFromCloudToVigboAlbum.type, handleSelectItemsFromCloud);
    yield* takeEvery(selectVigboAlbumAndAddItems.type, handleSelectVigboAlbumAndAddItems);
    yield* takeEvery(addFilesSuccess.type, handleAddFilesFromDevice);
}
